/**
 * ParallaxBackground
 * ─────────────────
 * Renders three layers of SVG radial-gradient orbs that translate at
 * different speeds relative to window.scrollY, creating a cinematic
 * depth illusion.  Fully pointer-events-none and reduced-motion safe.
 */

import React, { useEffect, useRef } from 'react';
import { useMotionValue, motion, useTransform } from 'framer-motion';

interface OrbDef {
  cx: string;   // % as string like "15%"
  cy: string;
  rx: string;
  ry: string;
  color: string;
  opacity: number;
  speed: number; // 0–1 parallax multiplier (higher = faster)
}

const ORBS: OrbDef[] = [
  // Layer 0 — slowest (background)
  { cx: '12%',  cy: '8%',  rx: '38%', ry: '32%', color: 'rgba(124,58,237,0.13)', opacity: 1, speed: 0.08 },
  { cx: '88%',  cy: '6%',  rx: '30%', ry: '26%', color: 'rgba(56,189,248,0.10)', opacity: 1, speed: 0.08 },
  // Layer 1 — medium
  { cx: '72%',  cy: '52%', rx: '22%', ry: '20%', color: 'rgba(0,229,255,0.07)',   opacity: 1, speed: 0.18 },
  { cx: '24%',  cy: '68%', rx: '28%', ry: '24%', color: 'rgba(139,92,246,0.08)',  opacity: 1, speed: 0.18 },
  // Layer 2 — fastest (foreground accent)
  { cx: '50%',  cy: '95%', rx: '26%', ry: '20%', color: 'rgba(16,185,129,0.06)',  opacity: 1, speed: 0.32 },
  { cx: '8%',   cy: '45%', rx: '16%', ry: '14%', color: 'rgba(245,158,11,0.05)', opacity: 1, speed: 0.32 },
];

// Group orbs by layer speed
const layers = [0.08, 0.18, 0.32];

const ParallaxBackground: React.FC = () => {
  const scrollY = useMotionValue(0);
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (prefersReducedMotion) return;
    const onScroll = () => scrollY.set(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [scrollY, prefersReducedMotion]);

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {layers.map((speed) =>
        ORBS
          .filter((o) => o.speed === speed)
          .map((orb, i) => (
            <ParallaxOrb key={`${speed}-${i}`} orb={orb} scrollY={scrollY} prefersReducedMotion={prefersReducedMotion} />
          ))
      )}
    </div>
  );
};

const ParallaxOrb: React.FC<{
  orb: OrbDef;
  scrollY: ReturnType<typeof useMotionValue<number>>;
  prefersReducedMotion: boolean;
}> = ({ orb, scrollY, prefersReducedMotion }) => {
  const y = useTransform(
    scrollY,
    [0, 2000],
    [0, prefersReducedMotion ? 0 : -(2000 * orb.speed)]
  );

  return (
    <motion.div
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        y,
        willChange: 'transform',
      }}
    >
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%' }}
      >
        <ellipse
          cx={orb.cx}
          cy={orb.cy}
          rx={orb.rx}
          ry={orb.ry}
          fill={orb.color}
          opacity={orb.opacity}
        />
      </svg>
    </motion.div>
  );
};

export default ParallaxBackground;
